import {
  FaBalanceScale,
  FaBrain,
  FaChartLine,
  FaClipboardList,
  FaFileAlt,
  FaHandshake,
  FaHeadset,
  FaIndustry,
  FaLaptopMedical,
  FaMicrochip,
  FaRobot,
  FaSearch,
  FaShieldAlt,
  FaSmile,
  FaThLarge,
  FaShoppingCart,
  FaTruck,
  FaUsers,
} from "react-icons/fa";

/** URL slug → backend service_code (PAS catalogue). */
export const PROCUREMENT_SERVICE_CODE_BY_SLUG = {
  "supplier-discovery": "supplier_discovery_reports",
  "managed-procurement": "managed_procurement_services",
  "procurement-retainer": "procurement_retainer_services",
};

export const PROCUREMENT_SERVICE_SLUGS = Object.keys(PROCUREMENT_SERVICE_CODE_BY_SLUG);

export const PROCUREMENT_SERVICE_ORDER = [
  "supplier-discovery",
  "managed-procurement",
  "procurement-retainer",
];

export const getProcurementServiceSlug = (serviceCode) =>
  Object.entries(PROCUREMENT_SERVICE_CODE_BY_SLUG).find(
    ([, code]) => code === serviceCode,
  )?.[0] || null;

export const getProcurementServiceCode = (slug) =>
  PROCUREMENT_SERVICE_CODE_BY_SLUG[slug] || null;

export const PAS_PROCESS_STEPS = [
  {
    step: 1,
    title: "Submit Requirement",
    description: "Share specs, quantity, and delivery needs",
    Icon: FaClipboardList,
  },
  {
    step: 2,
    title: "Supplier Discovery",
    description: "We identify and verify suitable suppliers",
    Icon: FaSearch,
  },
  {
    step: 3,
    title: "Quote Comparison",
    description: "Collect and compare supplier quotations",
    Icon: FaBalanceScale,
  },
  {
    step: 4,
    title: "Negotiation & Award",
    description: "Support negotiation and supplier selection",
    Icon: FaHandshake,
  },
  {
    step: 5,
    title: "Order Execution",
    description: "Coordinate PO, production, and dispatch",
    Icon: FaTruck,
  },
  {
    step: 6,
    title: "Delivery & Support",
    description: "Track delivery and resolve issues",
    Icon: FaHeadset,
  },
];

export const PAS_AI_AGENTS = [
  {
    key: "matching",
    title: "Smart Supplier Matching",
    description: "Matches your requirements with verified suppliers by category, capacity, and location.",
    Icon: FaBrain,
  },
  {
    key: "price",
    title: "Price Intelligence",
    description: "Benchmarks pricing, MOQ, and lead times to help you negotiate with confidence.",
    Icon: FaChartLine,
  },
  {
    key: "risk",
    title: "Risk & Compliance",
    description: "Flags pricing anomalies, delivery risks, and supplier reliability concerns early.",
    Icon: FaShieldAlt,
  },
  {
    key: "performance",
    title: "Performance Insights",
    description: "Tracks supplier performance, savings trends, and procurement KPIs over time.",
    Icon: FaRobot,
  },
];

export const PAS_STATS = [
  { value: "2,500+", label: "Verified Suppliers", Icon: FaUsers },
  { value: "150+", label: "Product Categories", Icon: FaThLarge },
  { value: "94%", label: "Customer Satisfaction", Icon: FaSmile },
  { value: "15 Days", label: "Avg. Turnaround Time", Icon: FaTruck },
];

export const PAS_INDUSTRIES = [
  { key: "industrial", label: "Industrial Supplies", Icon: FaIndustry },
  { key: "packaging", label: "Packaging & Printing", Icon: FaFileAlt },
  { key: "construction", label: "Construction Materials", Icon: FaTruck },
  { key: "furniture", label: "Furniture & Interiors", Icon: FaUsers },
  { key: "corporate", label: "Corporate Procurement", Icon: FaClipboardList },
  { key: "manufacturing", label: "Manufacturing & OEM", Icon: FaRobot },
  { key: "technology", label: "Technology Hardware", Icon: FaMicrochip },
  { key: "healthcare", label: "Healthcare & Pharma", Icon: FaLaptopMedical },
];

export const PAS_HERO_FEATURES = [
  "Verified & Trusted Suppliers",
  "Data-Driven Decisions",
  "End-to-End Support",
];

/** Shared + per-service page content. */
export const PROCUREMENT_SERVICE_PAGES = {
  "supplier-discovery": {
    slug: "supplier-discovery",
    serviceCode: "supplier_discovery_reports",
    seo: {
      title: "Supplier Discovery Reports | Trocanet Procurement Services",
      description:
        "Get professionally researched supplier shortlists with MOQ comparison, pricing benchmarks, and risk indicators.",
      canonicalPath: "/procurement-services/supplier-discovery",
    },
    hero: {
      eyebrow: "Supplier Discovery Reports",
      title: "Verified Supplier Shortlists for Confident Sourcing",
      description:
        "Receive professionally researched supplier lists tailored to your product, industry, and delivery requirements — so you can source faster with less risk.",
      primaryCta: "Request Supplier Report",
      secondaryCta: "Talk to an Expert",
    },
    package: {
      Icon: FaSearch,
      visualVariant: "discovery",
      imageUrl:
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80",
      title: "Supplier Discovery Reports",
      description:
        "Get professionally researched supplier shortlists with MOQ comparison, pricing benchmarks, and risk indicators.",
      features: [
        "Verified supplier list",
        "MOQ comparison",
        "Pricing benchmarks",
        "Supplier risk indicators",
        "Delivery capability analysis",
      ],
      bestFor: "SMEs and first-time buyers",
      pricingLabel: "Starting from",
      pricingAmount: "₹9,999",
      pricingSuffix: null,
      ctaLabel: "Request Discovery Report",
    },
  },
  "managed-procurement": {
    slug: "managed-procurement",
    serviceCode: "managed_procurement_services",
    seo: {
      title: "Managed Procurement Services | Trocanet Procurement Services",
      description:
        "End-to-end procurement execution — RFQ management, negotiation, quality coordination, and logistics support.",
      canonicalPath: "/procurement-services/managed-procurement",
    },
    hero: {
      eyebrow: "Managed Procurement Services",
      title: "End-to-End Procurement Execution for Growing Businesses",
      description:
        "Our team handles supplier communication, negotiation, coordination, inspection, and procurement execution so you can focus on your core business.",
      primaryCta: "Start Procurement Project",
      secondaryCta: "Talk to an Expert",
    },
    package: {
      Icon: FaHandshake,
      visualVariant: "managed",
      imageUrl:
        "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80",
      title: "Managed Procurement Services",
      description:
        "End-to-end procurement management from RFQ to delivery with expert negotiation and quality coordination.",
      features: [
        "End-to-end procurement management",
        "RFQ management",
        "Quote comparison",
        "Negotiation support",
        "Quality coordination",
        "Logistics coordination",
      ],
      bestFor: "Ongoing procurement",
      pricingLabel: "Service fee",
      pricingAmount: "3%–7%",
      pricingSuffix: "of procurement value",
      ctaLabel: "Start Procurement Project",
    },
  },
  "procurement-retainer": {
    slug: "procurement-retainer",
    serviceCode: "procurement_retainer_services",
    seo: {
      title: "Procurement Retainer Services | Trocanet Procurement Services",
      description:
        "Dedicated procurement manager, monthly sourcing pipeline, analytics, and priority support on retainer.",
      canonicalPath: "/procurement-services/procurement-retainer",
    },
    hero: {
      eyebrow: "Procurement Retainer Services",
      title: "Dedicated Procurement Support on Retainer",
      description:
        "Get a dedicated procurement manager, recurring sourcing pipeline, and analytics dashboard for businesses with ongoing procurement needs.",
      primaryCta: "Talk to Our Team",
      secondaryCta: "Schedule Consultation",
    },
    package: {
      Icon: FaChartLine,
      visualVariant: "retainer",
      imageUrl:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80",
      title: "Procurement Retainer Services",
      description:
        "Dedicated procurement manager with monthly sourcing pipeline, analytics, and priority support.",
      features: [
        "Dedicated procurement manager",
        "Monthly sourcing pipeline",
        "Procurement analytics dashboard",
        "Supplier performance tracking",
        "Priority procurement support",
      ],
      bestFor: "Mid-sized & large SMEs",
      pricingLabel: "Starting from",
      pricingAmount: "₹50,000",
      pricingSuffix: "per month",
      ctaLabel: "Talk to Our Team",
    },
  },
};

export const PROCUREMENT_LANDING_SEO = {
  title: "AI-Powered Procurement Services | Trocanet",
  description:
    "Smarter procurement and stronger supply chains — supplier discovery, managed procurement, and retainer services powered by AI-driven intelligence.",
  canonicalPath: "/procurement-services",
};

export const PROCUREMENT_LANDING_HERO = {
  eyebrow: "AI-POWERED PROCUREMENT SERVICES",
  title: "Smarter Procurement. Stronger Supply Chains.",
  description:
    "Trocanet helps businesses discover verified suppliers, compare quotes, negotiate confidently, and manage procurement — powered by expert teams and AI-driven intelligence.",
  primaryCta: "Explore Our Services",
  secondaryCta: "Talk to an Expert",
};

export const PROCUREMENT_CTA = {
  title: "Ready to Transform Your Procurement?",
  description:
    "Let our experts and AI platform help you source better, faster, and with confidence.",
  primaryCta: "Get Started Today",
  secondaryCta: "Schedule a Consultation",
};

export const listProcurementServicePages = () =>
  PROCUREMENT_SERVICE_SLUGS.map((slug) => PROCUREMENT_SERVICE_PAGES[slug]);

export const getProcurementServicePage = (slug) =>
  PROCUREMENT_SERVICE_PAGES[slug] || null;
